/** A row's Mapping Status badge. */
export type MappingStatus = 'Failed' | 'Edited' | 'Validated*' | 'Validated';

/** Normalize undefined / '' to null so "blank" forms compare equal. */
function normalize(value: unknown): unknown {
  return value === undefined || value === '' ? null : value;
}

/**
 * Equality used for "did this value change?" — null/undefined/'' are equal,
 * numeric strings compare as numbers, everything else compares as strings.
 */
export function valuesEqual(first: unknown, second: unknown): boolean {
  const na = normalize(first);
  const nb = normalize(second);
  if (na === null && nb === null) return true;
  if (na === null || nb === null) return false;
  const fa = parseFloat(na as string);
  const fb = parseFloat(nb as string);
  if (Number.isNaN(fa) || Number.isNaN(fb)) return String(na) === String(nb);
  return fa === fb;
}

/** True when every control's current value equals its baseline value. */
export function rowValuesEqual(
  controlNames: string[],
  current: Record<string, unknown>,
  baseline: Record<string, unknown>
): boolean {
  return controlNames.every((name) => valuesEqual(current[name], baseline[name]));
}

export interface RowStatusInput {
  /** The control names that make up this row (the sheet supplies its own structure). */
  controlNames: string[];
  /** Current form raw values, keyed by control name. */
  currentValues: Record<string, unknown>;
  /** Last-submission baseline values, keyed by control name (FilingFormStateService.getOriginalSheetData). */
  lastSubmissionValues: Record<string, unknown>;
  /** Component-computed: invalid control / missing mandatory / equality / cross-sheet error. */
  hasError: boolean;
  /** Component-computed: a "Suggested Change" warning applies (e.g. extracted-sum mismatch). */
  hasSuggestedChangeWarning: boolean;
  /** Force the Edited branch even if values match — used while a row is open in edit mode. */
  forceEdited?: boolean;
  /**
   * The row is an inherited, read-only mirror of another sheet's value (a
   * `linkTo` field, e.g. role-12 Revenue pulled from role-13). It can never be
   * user-edited on this sheet, and its value is deliberately filtered out of
   * this sheet's persisted baseline — so the value-deviation comparison below
   * would otherwise always report a spurious change. When true, the Edited
   * branch is skipped entirely and the row falls through to Failed / Validated* /
   * Validated. Defaults to false (every non-inherited row is unaffected).
   */
  inherited?: boolean;
  /**
   * The row is auto-DERIVED (a calculated total / readonly sum), not user-editable
   * (`enterEditMode` skips calculated/readonly fields). Its computed value is absent
   * from the persisted baseline, so the value-deviation check below would otherwise
   * report a spurious change. When true, the Edited branch is skipped (same treatment
   * as {@link inherited}); the row falls through to Failed / Validated* / Validated.
   * Defaults to false.
   */
  derived?: boolean;
}

/**
 * Decide a row's Mapping Status. Priority (first match wins): Edited > Failed > Validated* > Validated.
 * Edited = the row deviates from the last submission (or is being edited); it outranks errors/warnings.
 *
 * Two skip flags suppress the Edited branch because their values are deliberately
 * absent from this sheet's persisted baseline (so a deviation check would lie):
 * {@link RowStatusInput.inherited} (read-only `linkTo` mirror of another sheet) and
 * {@link RowStatusInput.derived} (a calculated total / readonly sum). Neither row can
 * be user-edited, so neither can ever be legitimately "Edited".
 */
export function computeRowStatus(input: RowStatusInput): MappingStatus {
  // Inherited and derived rows can never be "Edited" here (read-only mirror of
  // another sheet's value, or a client-computed total — both absent from this
  // sheet's baseline) — short-circuit before the value comparison so an absent
  // baseline can't masquerade as a user change.
  const deviates =
    input.inherited !== true &&
    input.derived !== true &&
    (input.forceEdited === true ||
      !rowValuesEqual(input.controlNames, input.currentValues, input.lastSubmissionValues));
  if (deviates) return 'Edited';
  if (input.hasError) return 'Failed';
  if (input.hasSuggestedChangeWarning) return 'Validated*';
  return 'Validated';
}
